import axios from 'axios'
import { useEffect, useMemo, useState } from 'react'
import { BookUser, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Avatar } from '@/components/common/Avatar'
import { Badge } from '@/components/common/Badge'
import { Button } from '@/components/common/Button'
import { Card } from '@/components/common/Card'
import { Input } from '@/components/common/Input'
import { Modal } from '@/components/common/Modal'
import { PageSpinner } from '@/components/common/Spinner'
import { useAuth } from '@/hooks/useAuth'
import ContactsService from '@/services/contacts.service'
import { useInstancesStore } from '@/stores/instancesStore'
import type { AvatarStatus, Contact } from '@/types'
import { getContactIdentityKey } from '@/utils/contactIdentity'
import { formatInternationalLeadNumber } from '@/utils/formatters'

interface ContactFormState {
  instance_id: string
  name: string
  phone_number: string
  avatar_url: string
  blocked: boolean
}

const emptyContactForm: ContactFormState = {
  instance_id: '',
  name: '',
  phone_number: '',
  avatar_url: '',
  blocked: false,
}

const AVATAR_STATUS_LABEL: Record<AvatarStatus, string> = {
  fetched: 'Foto sincronizada',
  pending: 'Foto pendente',
  not_found: 'Sem foto',
  error: 'Falha na sincronização',
}

function getAvatarBadgeVariant(status?: AvatarStatus) {
  switch (status) {
    case 'fetched':
      return 'success'
    case 'error':
      return 'error'
    case 'not_found':
      return 'default'
    default:
      return 'warning'
  }
}

function getDeleteErrorMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) return 'Não foi possível remover o contato.'

  const status = error.response?.status
  const detail = error.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail

  switch (status) {
    case 403:
      return 'Você não tem permissão para remover este contato.'
    case 404:
      return 'Contato não encontrado ou já removido.'
    case 409:
      return 'Não foi possível remover o contato porque existem dependências relacionadas.'
    default:
      return 'Não foi possível remover o contato.'
  }
}

export default function ContactsPage() {
  const { can } = useAuth()
  const canManageContacts = can('manage_instances')
  const { instances, fetchInstances } = useInstancesStore()
  const [contacts, setContacts] = useState<Contact[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedInstanceId, setSelectedInstanceId] = useState('all')
  const [avatarStatusFilter, setAvatarStatusFilter] = useState<'all' | AvatarStatus>('all')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null)
  const [editingContact, setEditingContact] = useState<Contact | null>(null)
  const [form, setForm] = useState<ContactFormState>(emptyContactForm)

  const instanceOptions = useMemo(
    () =>
      [...instances].sort((left, right) => {
        if (left.status === right.status) return left.name.localeCompare(right.name)
        if (left.status === 'connected') return -1
        if (right.status === 'connected') return 1
        return left.name.localeCompare(right.name)
      }),
    [instances],
  )

  const visibleContacts = useMemo(() => {
    const grouped = new Map<string, Contact>()

    for (const contact of contacts) {
      const key = getContactIdentityKey(contact)
      const current = grouped.get(key)
      if (!current) {
        grouped.set(key, contact)
        continue
      }

      const currentTime = current.updated_at ? new Date(current.updated_at).getTime() : 0
      const incomingTime = contact.updated_at ? new Date(contact.updated_at).getTime() : 0
      if (incomingTime >= currentTime) {
        grouped.set(key, contact)
      }
    }

    return [...grouped.values()]
  }, [contacts])

  async function loadContacts() {
    setIsLoading(true)
    try {
      const response = await ContactsService.getAll({
        search: search || undefined,
        instance_id: selectedInstanceId === 'all' ? undefined : selectedInstanceId,
        avatar_status: avatarStatusFilter === 'all' ? undefined : avatarStatusFilter,
        per_page: 100,
      })
      setContacts(response.data)
    } catch {
      toast.error('Não foi possível carregar os contatos.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchInstances().catch(() => undefined)
  }, [fetchInstances])

  useEffect(() => {
    loadContacts().catch(() => undefined)
  }, [search, selectedInstanceId, avatarStatusFilter])

  function resetForm() {
    setForm({
      ...emptyContactForm,
      instance_id:
        selectedInstanceId !== 'all'
          ? selectedInstanceId
          : instanceOptions[0]?.id ?? '',
    })
  }

  function openCreateModal() {
    setEditingContact(null)
    resetForm()
    setIsModalOpen(true)
  }

  function openEditModal(contact: Contact) {
    setEditingContact(contact)
    setForm({
      instance_id: contact.instance_id,
      name: contact.name ?? contact.push_name ?? '',
      phone_number: contact.phone_number ?? contact.jid.replace('@s.whatsapp.net', ''),
      avatar_url: contact.avatar_url ?? '',
      blocked: contact.blocked,
    })
    setIsModalOpen(true)
  }

  async function handleSubmit() {
    if (!form.instance_id || !form.phone_number.trim()) {
      toast.error('Selecione a instância e informe o número do lead.')
      return
    }

    setIsSaving(true)
    try {
      if (editingContact) {
        const updated = await ContactsService.update(editingContact.id, {
          name: form.name.trim() || undefined,
          phone_number: form.phone_number.trim(),
          avatar_url: form.avatar_url.trim() || undefined,
          blocked: form.blocked,
        })
        setContacts((current) =>
          current.map((contact) => (contact.id === updated.id ? updated : contact)),
        )
        toast.success('Contato atualizado com sucesso.')
      } else {
        const created = await ContactsService.create({
          instance_id: form.instance_id,
          name: form.name.trim() || undefined,
          phone_number: form.phone_number.trim(),
          avatar_url: form.avatar_url.trim() || undefined,
          blocked: form.blocked,
        })
        setContacts((current) => [created, ...current.filter((item) => item.id !== created.id)])
        toast.success('Contato criado com sucesso.')
      }
      setIsModalOpen(false)
      await loadContacts()
    } catch {
      toast.error('Não foi possível salvar o contato.')
    } finally {
      setIsSaving(false)
    }
  }

  async function handleDelete() {
    if (!contactToDelete) return

    setIsDeleting(true)
    try {
      await ContactsService.delete(contactToDelete.id)
      setContacts((current) => current.filter((contact) => contact.id !== contactToDelete.id))
      setContactToDelete(null)
      toast.success('Contato removido com sucesso.')
      await loadContacts()
    } catch (error) {
      toast.error(getDeleteErrorMessage(error))
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading && visibleContacts.length === 0) {
    return <PageSpinner label="Carregando contatos..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <p className="text-sm text-[var(--text-muted)]">
            Gerencie leads do tenant ativo, filtre por instância e execute ações por linha.
          </p>
          <p className="text-sm text-[var(--text-muted)]">
            {visibleContacts.length} contato{visibleContacts.length === 1 ? '' : 's'} carregado{visibleContacts.length === 1 ? '' : 's'}.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => loadContacts().catch(() => undefined)}>
            Atualizar
          </Button>
          {canManageContacts && (
            <Button icon={<Plus size={14} />} onClick={openCreateModal}>
              Novo contato
            </Button>
          )}
        </div>
      </div>

      <Card className="space-y-4 p-5">
        <div className="grid gap-4 xl:grid-cols-[1.5fr,1fr,1fr]">
          <Input
            label="Buscar"
            placeholder="Nome, telefone ou JID"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            leftIcon={<Search size={16} />}
          />

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[var(--text-primary)]">Instância</label>
            <select
              value={selectedInstanceId}
              onChange={(event) => setSelectedInstanceId(event.target.value)}
            >
              <option value="all">Todas as instâncias</option>
              {instanceOptions.map((instance) => (
                <option key={instance.id} value={instance.id}>
                  {instance.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[var(--text-primary)]">Status da foto</label>
            <select
              value={avatarStatusFilter}
              onChange={(event) => setAvatarStatusFilter(event.target.value as 'all' | AvatarStatus)}
            >
              <option value="all">Todos</option>
              <option value="fetched">Sincronizada</option>
              <option value="pending">Pendente</option>
              <option value="not_found">Sem foto</option>
              <option value="error">Erro</option>
            </select>
          </div>
        </div>
      </Card>

      <div className="grid gap-4">
        {visibleContacts.map((contact) => {
          const instanceName =
            instanceOptions.find((instance) => instance.id === contact.instance_id)?.name ??
            'Instância removida'
          const displayName = contact.push_name ?? contact.name ?? 'Lead sem nome'
          const leadNumber = formatInternationalLeadNumber(
            contact.phone_number ?? contact.jid.replace('@s.whatsapp.net', ''),
          )

          return (
            <Card key={contact.id} className="p-6">
              <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                <div className="flex items-start gap-4">
                  <Avatar
                    src={contact.avatar_url}
                    name={displayName}
                    size="xl"
                    online={!contact.blocked}
                  />

                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3>{displayName}</h3>
                      <Badge variant={contact.blocked ? 'error' : 'success'}>
                        {contact.blocked ? 'Bloqueado' : 'Ativo'}
                      </Badge>
                      <Badge variant={getAvatarBadgeVariant(contact.avatar_status)}>
                        {AVATAR_STATUS_LABEL[contact.avatar_status ?? 'pending']}
                      </Badge>
                    </div>

                    <p className="text-base font-medium text-[var(--text-primary)]">{leadNumber}</p>
                    <p className="text-sm text-[var(--text-muted)]">Instância: {instanceName}</p>
                    <p className="text-sm text-[var(--text-muted)]">JID: {contact.jid}</p>
                  </div>
                </div>

                {canManageContacts && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      icon={<Pencil size={14} />}
                      onClick={() => openEditModal(contact)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="danger"
                      icon={<Trash2 size={14} />}
                      onClick={() => setContactToDelete(contact)}
                    >
                      Excluir
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          )
        })}

        {!isLoading && visibleContacts.length === 0 && (
          <Card className="p-8">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-[var(--color-brand-light)] p-3 text-[var(--color-brand)]">
                <BookUser size={20} />
              </div>
              <div>
                <h3>Nenhum contato encontrado</h3>
                <p className="text-sm text-[var(--text-muted)]">
                  Ajuste os filtros ou crie um lead para começar.
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingContact ? 'Editar contato' : 'Novo contato'}
        description="Formulário responsivo padrão para criação e edição de contatos."
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[var(--text-primary)]">Instância</label>
            <select
              value={form.instance_id}
              onChange={(event) =>
                setForm((current) => ({ ...current, instance_id: event.target.value }))
              }
              disabled={Boolean(editingContact)}
            >
              <option value="">Selecione</option>
              {instanceOptions.map((instance) => (
                <option key={instance.id} value={instance.id}>
                  {instance.name}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="Nome do lead"
            placeholder="Ex.: Joel Quintana"
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          />

          <Input
            label="Número internacional"
            placeholder="5567992466329"
            value={form.phone_number}
            onChange={(event) =>
              setForm((current) => ({ ...current, phone_number: event.target.value }))
            }
          />

          <Input
            label="Avatar URL"
            placeholder="https://..."
            value={form.avatar_url}
            onChange={(event) =>
              setForm((current) => ({ ...current, avatar_url: event.target.value }))
            }
          />

          <label className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
            <input
              type="checkbox"
              checked={form.blocked}
              onChange={(event) =>
                setForm((current) => ({ ...current, blocked: event.target.checked }))
              }
            />
            Marcar como bloqueado
          </label>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={() => handleSubmit().catch(() => undefined)} loading={isSaving}>
              {editingContact ? 'Salvar alterações' : 'Criar contato'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={contactToDelete !== null}
        onClose={() => !isDeleting && setContactToDelete(null)}
        title="Excluir contato"
        description="A remoção valida o tenant ativo e atualiza a lista imediatamente."
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-[var(--text-secondary)]">
            Deseja remover o contato <strong>{contactToDelete?.name ?? contactToDelete?.jid}</strong>?
          </p>
          <div className="flex items-center justify-end gap-3">
            <Button variant="ghost" onClick={() => setContactToDelete(null)} disabled={isDeleting}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => handleDelete().catch(() => undefined)}
              loading={isDeleting}
            >
              Excluir
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
